import moment from "moment";
import { Button } from "risa-oasis-ui_v2";
import { BiosimilarConfigSavedMeta } from "../types";

/**
 * Props for the drug family configuration footer.
 */
interface BiosimilarConfigFooterProps {
  /** Total families in the document. */
  familyCount: number;
  /** Families that carry dose conversions. */
  doseConversionCount: number;
  /** Audit fields from the last saved document. */
  lastSavedMeta: BiosimilarConfigSavedMeta | null;
  /** Whether there are unsaved edits. */
  isDirty: boolean;
  /** Whether a save is in flight. */
  saving: boolean;
  /** Persists changes to Firestore. */
  onSave: () => void;
  /** Reverts edits back to the last loaded document. */
  onReset: () => void;
}

/**
 * Footer with family counts, last-updated audit line, and reset/save actions.
 *
 * @param props - Counts, audit metadata, and action handlers
 * @returns Footer row matching other configuration pages
 */
const BiosimilarConfigFooter = ({
  familyCount,
  doseConversionCount,
  lastSavedMeta,
  isDirty,
  saving,
  onSave,
  onReset,
}: BiosimilarConfigFooterProps) => {
  const formattedUpdatedAt =
    lastSavedMeta?.updated_at != null
      ? moment(lastSavedMeta.updated_at).format("DD MMM YYYY HH:mm")
      : null;
  const hasMeta =
    lastSavedMeta != null &&
    (lastSavedMeta.updated_at != null || lastSavedMeta.updated_by != null);

  return (
    <div className="flex flex-shrink-0 flex-row items-center justify-between gap-4 border-t border-primaryGray-16 pt-3">
      <div className="flex flex-col gap-1 text-tiny text-primaryGray-7">
        <div>
          {familyCount} {familyCount === 1 ? "family" : "families"} ·{" "}
          {doseConversionCount} with dose conversions
        </div>
        {hasMeta ? (
          <div>
            Last updated:{" "}
            {formattedUpdatedAt != null && (
              <span className="font-semibold">{formattedUpdatedAt}</span>
            )}
            {formattedUpdatedAt != null && lastSavedMeta?.updated_by != null && (
              <span> by </span>
            )}
            {lastSavedMeta?.updated_by != null && (
              <span className="font-semibold">{lastSavedMeta.updated_by}</span>
            )}
          </div>
        ) : (
          <div className="text-primaryGray-12">Not saved yet</div>
        )}
      </div>

      <div className="flex flex-row gap-2">
        <Button
          buttonType="secondary"
          size="medium"
          onClick={onReset}
          disabled={!isDirty || saving}
          children="Reset"
        />
        <Button
          buttonType="primary"
          size="medium"
          onClick={onSave}
          disabled={!isDirty || saving}
          children={saving ? "Saving..." : "Save Changes"}
        />
      </div>
    </div>
  );
};

export default BiosimilarConfigFooter;
